import { useState, useEffect, useCallback, useMemo } from 'react';
import DataTablePage from 'components/DataTablePage';
import { Navigate } from 'react-router-dom';
import { Can } from 'context';
import gamesColumnData from 'data/gameColumnData';
import { getGames } from 'services/games';
import { getAutocompleteClients } from 'services/clients';
import { FormControl, InputLabel, Select, MenuItem, Icon, InputAdornment } from '@mui/material';
import MDBox from 'components/MDBox';

function GamesFilterComponent({ onFilterChange }) {
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState('');
  const [loading, setLoading] = useState(false);

  const loadClients = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getAutocompleteClients('');
      setClients(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error loading clients:', err);
      setClients([]);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    loadClients();
  }, [loadClients]);

  const handleClientChange = (event) => {
    const value = event.target.value;
    setSelectedClient(value);
    onFilterChange(value);
  };

  return (
    <MDBox sx={{ display: 'flex', gap: 1.5, marginLeft: 'auto', alignItems: 'center' }}>
      {selectedClient && (
        <MDBox
          sx={{
            display: 'flex', alignItems: 'center', gap: 0.5,
            fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.04em',
            color: '#22d3ee', opacity: 0.85, textTransform: 'uppercase'
          }}
        >
          <Icon sx={{ fontSize: '0.9rem' }}>filter_alt</Icon>
          Filtered
        </MDBox>
      )}
      <FormControl
        size='small'
        sx={{
          minWidth: 220,
          '& .MuiOutlinedInput-root': { borderRadius: '10px', height: 38 },
          '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: '#22d3ee'
          }
        }}
      >
        <InputLabel id='client-filter-label'>Integration</InputLabel>
        <Select
          labelId='client-filter-label'
          value={selectedClient}
          onChange={handleClientChange}
          label='Integration'
          disabled={loading}
          startAdornment={
            <InputAdornment position='start'>
              <Icon sx={{ fontSize: '1rem', opacity: 0.5 }}>business</Icon>
            </InputAdornment>
          }
        >
          <MenuItem value=''>
            <em>All Integrations</em>
          </MenuItem>
          {clients.map((client) => (
            <MenuItem key={client.value} value={client.label}>
              {client.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </MDBox>
  );
}

function Games() {
  const [clientFilter, setClientFilter] = useState('');

  const queryKey = useMemo(() => ['games', clientFilter], [clientFilter]);

  const fetchFilteredGames = useCallback(
    async (limit, page) => {
      const result = await getGames(limit, page);

      if (clientFilter && result?.data) {
        const filtered = result.data.filter((game) => game.client?.name?.toLowerCase() === clientFilter.toLowerCase());
        return {
          ...result,
          data: filtered,
          meta: {
            ...result.meta,
            totalItems: filtered.length,
            itemCount: filtered.length
          }
        };
      }

      return result;
    },
    [clientFilter]
  );

  return (
    <>
      <Can I='read' a='game'>
        <DataTablePage
          title='Games'
          canSearch
          canFilter
          fetchData={fetchFilteredGames}
          queryKey={queryKey}
          columnData={gamesColumnData}
          object={'game'}
          noActions={true}
          filtersComponent={<GamesFilterComponent onFilterChange={setClientFilter} />}
        />
      </Can>
      <Can not I='read' a='game'>
        <Navigate to='/dashboard' replace />
      </Can>
    </>
  );
}

export default Games;
